import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import Avvvatars from "avvvatars-react";
import { cn } from "@/lib/utils";

const EnhancedAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
));
EnhancedAvatar.displayName = AvatarPrimitive.Root.displayName;

const EnhancedAvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
EnhancedAvatarImage.displayName = AvatarPrimitive.Image.displayName;

interface EnhancedAvatarFallbackProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  /** Value to generate unique avvvatars - typically email or name */
  value?: string;
  /** Style variant for avvvatars */
  style?: "character" | "shape";
  /** Disable avvvatars and use traditional fallback */
  disableAvvvatars?: boolean;
}

const EnhancedAvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  EnhancedAvatarFallbackProps
>(({ className, value, style = "character", disableAvvvatars = false, children, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full",
      // Only apply bg-muted if we're using traditional fallback
      disableAvvvatars || !value ? "bg-muted" : "",
      className
    )}
    {...props}
  >
    {!disableAvvvatars && value ? (
      <Avvvatars 
        value={value} 
        style={style}
        size={32} // Default size, will be overridden by CSS
      />
    ) : (
      children
    )}
  </AvatarPrimitive.Fallback>
));
EnhancedAvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// Utility function to generate initials from first and last name
const generateInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.trim().charAt(0).toUpperCase() || '';
  const last = lastName?.trim().charAt(0).toUpperCase() || '';
  return (first + last).slice(0, 2) || '?';
};

// Convenience component that combines all three
interface UserAvatarProps {
  /** Profile image URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Value to generate unique avvvatars - typically email or name */
  value?: string;
  /** Style variant for avvvatars */
  style?: "character" | "shape";
  /** Traditional fallback text (initials) - will be auto-generated if firstName/lastName provided */
  fallbackText?: string;
  /** First name for generating initials */
  firstName?: string;
  /** Last name for generating initials */
  lastName?: string;
  /** Size classes for the avatar */
  className?: string;
  /** Disable avvvatars and use traditional fallback */
  disableAvvvatars?: boolean;
}

const UserAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  UserAvatarProps
>(({ src, alt, value, style = "character", fallbackText, firstName, lastName, className, disableAvvvatars = false, ...props }, ref) => {
  // Auto-generate initials from firstName/lastName if provided, otherwise use fallbackText
  const initials = firstName || lastName ? generateInitials(firstName, lastName) : fallbackText;
  
  return (
    <EnhancedAvatar ref={ref} className={className} {...props}>
      {src && <EnhancedAvatarImage src={src} alt={alt} />}
      <EnhancedAvatarFallback 
        value={value} 
        style={style}
        disableAvvvatars={disableAvvvatars}
      >
        {initials}
      </EnhancedAvatarFallback>
    </EnhancedAvatar>
  );
});
UserAvatar.displayName = "UserAvatar";

export { 
  EnhancedAvatar, 
  EnhancedAvatarImage, 
  EnhancedAvatarFallback,
  UserAvatar
};